import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Stack,
  Box,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { X } from '@phosphor-icons/react';
import { useState, useEffect, useCallback } from 'react';
import useApiCallable from '../../api/use-api-callable';
import { toast } from 'sonner';
import { useInvalidation } from 'api-read-hook';
import { Api } from '../../api';
import SoloLoading from '../SoloLoading';

type Props = {
  open: boolean;
  onClose: () => void;
  userId: string;
  existingRecipePreferences: Api.UserStats.V['user']['recipePreferences'];
};

export default function EditDietModal({
  open,
  onClose,
  userId,
  existingRecipePreferences,
}: Props) {
  const callApi = useApiCallable();
  const { invalidateExact } = useInvalidation();

  const recipesApi = Api.Recipe.useReadCollection();

  const [selectedRecipes, setSelectedRecipes] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      setSelectedRecipes(existingRecipePreferences.map((r) => r['@id']));
    }
  }, [open, existingRecipePreferences]);

  const handleRecipeChange = useCallback(
    (recipeId: string, checked: boolean) => {
      if (checked) {
        setSelectedRecipes((prev) => [...prev, recipeId]);
      } else {
        setSelectedRecipes((prev) => prev.filter((id) => id !== recipeId));
      }
    },
    [],
  );

  const handleSave = async () => {
    await callApi(`/users/${userId}/as-admin`, {
      method: 'PATCH',
      jsonBody: { recipePreferences: selectedRecipes },
    });

    invalidateExact(`/users/${userId}/stats`);
    toast.success('Recipe preferences updated successfully');
    onClose();
  };

  if (recipesApi.error) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6">Edit Diet</Typography>
          <IconButton onClick={onClose} size="small">
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography color="error">Error loading recipes</Typography>
        </DialogContent>
      </Dialog>
    );
  }

  if (!recipesApi.data) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6">Edit Diet</Typography>
          <IconButton onClick={onClose} size="small">
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <SoloLoading />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6">Edit Diet</Typography>
        <IconButton onClick={onClose} size="small">
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Users have the ability to overwrite your changes in the app
          </Typography>

          <Box
            sx={{
              bgcolor: 'var(--mui-palette-background-level1)',
              borderRadius: 2,
              p: 3,
            }}
          >
            <Stack spacing={2}>
              <Typography variant="subtitle1">
                What types of recipes would you like to see?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Select all that apply
              </Typography>

              <Stack spacing={1}>
                {(recipesApi.data?.member || []).map((recipe) => (
                  <FormControlLabel
                    key={recipe['@id']}
                    control={
                      <Checkbox
                        checked={selectedRecipes.includes(recipe['@id'])}
                        onChange={(e) =>
                          handleRecipeChange(recipe['@id'], e.target.checked)
                        }
                      />
                    }
                    label={recipe.name}
                  />
                ))}
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleSave} variant="contained">
          Save Changes
        </Button>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
